/**
 * Settings container (original/src/App.tsx:41485-41596 → `Settings` screen). The category and the
 * card order are the viewer's (`ui.settings_order`, D-123 / D-124); the card → permission map is the
 * old `renderSettingsSection` guards (App.tsx:13075-16800) expressed in D-129 tokens — INFERRED for
 * the cards whose old guard was a role name (sub_safes_management) or absent (notifications, wallets,
 * capital, partners, super_currencies): they take the per-card settings token D-129 introduces.
 * B3 cards: appearance (layout), lists (menu_customization), security, users; C20: box management (wallets,
 * `BoxesContainer`); C21: currencies; C26: capital (`CapitalContainer`); C27: subscription (`SubscriptionContainer`, new card,
 * Super Manager only); the others are frames with the B4 placeholder.
 */
import { useState } from "react";
import { SETTINGS_CATEGORY_CARDS, SETTINGS_DEFAULT_ORDER, Settings, SettingsCardFrame, SettingsCardPlaceholder, type SettingsCardKey, type SettingsCategory } from "@faylasuf/ui";
import { useSession } from "../app/context.js";
import type { UiPrefs } from "../app/preferences.js";
import type { SectionFlags } from "../app/sections.js";
import { AppearanceContainer } from "./settings/AppearanceContainer.js";
import { MenuCustomizationContainer } from "./settings/MenuCustomizationContainer.js";
import { SecurityContainer } from "./settings/SecurityContainer.js";
import { CompanyContainer } from "./settings/CompanyContainer.js";
import { UsersContainer } from "./settings/UsersContainer.js";
import { BoxesContainer } from "./settings/BoxesContainer.js";
import { CurrenciesContainer } from "./settings/CurrenciesContainer.js";
import { CapitalContainer } from "./settings/CapitalContainer.js";
import { SubscriptionContainer } from "./settings/SubscriptionContainer.js";
import { MaintenanceContainer } from "./settings/MaintenanceContainer.js";

export const CARD_PERMISSION: Readonly<Record<SettingsCardKey, string | null>> = {
  layout: null, // App.tsx:13663 — no guard
  menu_customization: null, // App.tsx:13079 — no guard
  notifications: "settings_notifications", // INFERRED (D-129 per-card token; old: none)
  security: "security_codes", // App.tsx:14263
  users: "edit_user_details", // App.tsx:14807
  wallets: "settings_boxes", // INFERRED
  company: "company_settings", // App.tsx:15038
  office_storefront: "settings_storefront", // App.tsx:15380 used company_settings; D-129 adds the storefront token (INFERRED)
  capital: "manage_capital", // [C26] D-263 (c): the card holds only the capital actions (old guard `invested_capital`, App.tsx:15416)
  sub_safes_management: "settings_sub_safes", // App.tsx:15538 guarded by role name → D-129 token (INFERRED)
  partners_management: "settings_partners", // INFERRED
  digital_profits_management: "digital_profits_management", // App.tsx:16368
  super_currencies: "settings_currencies", // INFERRED
  maintenance: "system_maintenance", // App.tsx:16766
  subscription: null, // [C27] D-261: Super Manager only (the routes are superOnly); filtered below by role, not by a token
};

/** Default order overlaid with the viewer's `ui.settings_order` (D-124); unfiltered. */
export function fullSettingsOrder(settingsOrder: readonly string[] | null): SettingsCardKey[] {
  const order: SettingsCardKey[] = [];
  for (const k of settingsOrder ?? []) if ((SETTINGS_DEFAULT_ORDER as readonly string[]).includes(k)) order.push(k as SettingsCardKey);
  for (const k of SETTINGS_DEFAULT_ORDER) if (!order.includes(k)) order.push(k);
  return order;
}

export const settingsCardAllowed = (k: SettingsCardKey, hasPermission: (t: string) => boolean): boolean =>
  CARD_PERMISSION[k] === null || hasPermission(CARD_PERMISSION[k]!) || (k === "maintenance" && hasPermission("download_backup")); // [C38] the download lives in this card (D-151)

/** i18n label key per card, as the old lists card resolved them (App.tsx:13261-13297). */
export const SETTINGS_CARD_LABEL_KEY: Readonly<Record<SettingsCardKey, string>> = {
  layout: "layout",
  notifications: "settings.card_notifications",
  security: "settings.card_security",
  users: "userManagement",
  wallets: "walletManagement",
  company: "businessIdentity",
  office_storefront: "settings.card_storefront",
  capital: "capital",
  sub_safes_management: "settings.card_sub_safes",
  partners_management: "settings.card_partners",
  digital_profits_management: "settings.card_digital_profits",
  super_currencies: "settings.card_currencies",
  maintenance: "systemMaintenance",
  menu_customization: "menuCustomization",
  subscription: "settings.card_subscription",
};

export interface SettingsPageProps {
  readonly prefs: UiPrefs;
  readonly setPreference: (key: string, value: unknown, onError?: (e: unknown) => void) => void;
  readonly flags: SectionFlags;
  readonly onComingB4: () => void;
}

export function SettingsPage({ prefs, setPreference, flags, onComingB4 }: SettingsPageProps) {
  const { hasPermission, isSuperManager } = useSession();
  const [category, setCategory] = useState<SettingsCategory>("display");

  const cards = fullSettingsOrder(prefs.settingsOrder).filter((k) => SETTINGS_CATEGORY_CARDS[category].includes(k)).filter((k) => settingsCardAllowed(k, hasPermission)).filter((k) => k !== "subscription" || isSuperManager);

  return (
    <Settings category={category} onCategoryChange={setCategory}>
      {cards.map((k) => {
        const custom = prefs.icons[k];
        if (k === "layout") return <AppearanceContainer key={k} prefs={prefs} setPreference={setPreference} />;
        if (k === "security") return <SecurityContainer key={k} />;
        if (k === "users") return <UsersContainer key={k} />;
        if (k === "company") return <CompanyContainer key={k} {...(custom ? { customIcon: custom } : {})} />; // [C35]
        if (k === "wallets") return <BoxesContainer key={k} {...(custom ? { customIcon: custom } : {})} />;
        if (k === "super_currencies") return <CurrenciesContainer key={k} {...(custom ? { customIcon: custom } : {})} />;
        if (k === "capital") return <CapitalContainer key={k} subSafesActive={flags.subSafesActive} {...(custom ? { customIcon: custom } : {})} />;
        if (k === "maintenance") return <MaintenanceContainer key={k} {...(custom ? { customIcon: custom } : {})} />; // [C38] B6 part 2
        if (k === "subscription") return <SubscriptionContainer key={k} {...(custom ? { customIcon: custom } : {})} />;
        if (k === "menu_customization") return <MenuCustomizationContainer key={k} prefs={prefs} setPreference={setPreference} flags={flags} onComingB4={onComingB4} />;
        return (
          <SettingsCardFrame key={k} cardKey={k} {...(custom ? { customIcon: custom } : {})}>
            <SettingsCardPlaceholder />
          </SettingsCardFrame>
        );
      })}
    </Settings>
  );
}
